import { useEffect, useState, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import style from "./ScrollToTop.module.css";
import ArrowTop from "./../../assets/images/arrow_top.svg";

const SCROLL_THRESHOLD = 300;

const PULSE_HOST_ELEMENT_SELECTOR = 'sp-live-chat';
const PULSE_FAB_SELECTOR = '.widget-fab';
const PULSE_STYLED_CLASS = 'pulse-widget-customized';
const PULSE_SCRIPT_URL = 'https://cdn.pulse.is/livechat/loader.js';
const PULSE_CHAT_ID = '6628ce95fca5fee0aa08b5f5';
const PULSE_FAB_TEXT = '114 տեղեկատու';

const PULSE_INITIAL_VISIBILITY_CSS_ID = 'pulse-custom-visibility-styles';
const PULSE_INITIAL_VISIBILITY_CSS = `
  ${PULSE_HOST_ELEMENT_SELECTOR} {
    opacity: 0 !important; 
    bottom: -22px !important;
    transition: opacity 0.3s ease-in-out; 
  }
  ${PULSE_HOST_ELEMENT_SELECTOR}.${PULSE_STYLED_CLASS} {
    opacity: 1 !important; 
  }
`;

export const ScrollToTop = () => {
    const [isVisible, setIsVisible] = useState(false);
    const location = useLocation();

    const pulseObserverRef = useRef(null);
    const pulseIntervalIdRef = useRef(null);
    const pulseAssetsInjectedRef = useRef(false);
    const pulseFabStyledRef = useRef(false);

    const handleScroll = useCallback(() => {
        setIsVisible(window.scrollY > SCROLL_THRESHOLD);
    }, []);

    const scrollToTop = useCallback(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    useEffect(() => {
        window.addEventListener('scroll', handleScroll);

        if (!pulseAssetsInjectedRef.current) {
            if (!document.getElementById(PULSE_INITIAL_VISIBILITY_CSS_ID)) {
                const styleElement = document.createElement('style');
                styleElement.id = PULSE_INITIAL_VISIBILITY_CSS_ID;
                styleElement.innerHTML = PULSE_INITIAL_VISIBILITY_CSS;
                document.head.appendChild(styleElement);
            }

            if (!document.querySelector(`script[src="${PULSE_SCRIPT_URL}"]`)) {
                const script = document.createElement('script');
                script.src = PULSE_SCRIPT_URL;
                script.setAttribute('data-live-chat-id', PULSE_CHAT_ID);
                script.async = true;
                document.body.appendChild(script);
            }
            pulseAssetsInjectedRef.current = true;
        }


        if (pulseIntervalIdRef.current) clearInterval(pulseIntervalIdRef.current);
        if (pulseObserverRef.current) pulseObserverRef.current.disconnect();


        if (!pulseFabStyledRef.current) {
            let attempts = 0;
            const maxAttempts = 30;

            pulseIntervalIdRef.current = setInterval(() => {
                attempts++;
                const liveChatHostElement = document.querySelector(PULSE_HOST_ELEMENT_SELECTOR);
                const shadowRoot = liveChatHostElement?.shadowRoot;
                const fabElement = shadowRoot?.querySelector(PULSE_FAB_SELECTOR);

                if (liveChatHostElement && shadowRoot && fabElement) {
                    const applyCustomButtonStyles = (targetFab) => {
                        targetFab.textContent = PULSE_FAB_TEXT;
                        targetFab.style.backgroundColor = '#2f80ed';
                        targetFab.style.color = '#fff';
                        targetFab.style.fontSize = '16px';
                        targetFab.style.fontWeight = 'bold';
                        targetFab.style.borderRadius = '8px';
                        targetFab.style.padding = '0 16px';
                        targetFab.style.height = '40px';
                        targetFab.style.width = '300px';
                        targetFab.style.maxWidth = '100%';
                        targetFab.style.display = 'flex';
                        targetFab.style.alignItems = 'center';
                        targetFab.style.justifyContent = 'center';
                        targetFab.style.whiteSpace = 'nowrap';
                        targetFab.style.cursor = 'pointer';
                        targetFab.style.right = '80px';
                    };

                    applyCustomButtonStyles(fabElement);
                    liveChatHostElement.classList.add(PULSE_STYLED_CLASS);

                    if (pulseObserverRef.current) pulseObserverRef.current.disconnect();
                    pulseObserverRef.current = new MutationObserver(() => {
                        const currentShadowRoot = liveChatHostElement.shadowRoot;
                        const currentFab = currentShadowRoot?.querySelector(PULSE_FAB_SELECTOR);
                        if (currentFab && currentFab.textContent !== PULSE_FAB_TEXT) {
                            applyCustomButtonStyles(currentFab);
                            if (!liveChatHostElement.classList.contains(PULSE_STYLED_CLASS)) {
                                liveChatHostElement.classList.add(PULSE_STYLED_CLASS);
                            }
                        }
                    });
                    pulseObserverRef.current.observe(shadowRoot, {
                        childList: true, subtree: true, characterData: true, attributes: true
                    });

                    clearInterval(pulseIntervalIdRef.current);
                    pulseIntervalIdRef.current = null;
                    pulseFabStyledRef.current = true;

                } else if (attempts > maxAttempts) {
                    console.warn(`Pulse Live Chat FAB (${PULSE_FAB_SELECTOR}) not found after ${maxAttempts} attempts. Widget might remain hidden or use default styles if visibility class is added.`);
                    clearInterval(pulseIntervalIdRef.current);
                    pulseIntervalIdRef.current = null;
                    pulseFabStyledRef.current = true;
                }
            }, 200);
        }

        return () => {
            window.removeEventListener('scroll', handleScroll);
            if (pulseIntervalIdRef.current) {
                clearInterval(pulseIntervalIdRef.current);
            }
            if (pulseObserverRef.current) {
                pulseObserverRef.current.disconnect();
            }

        };
    }, [handleScroll]);

    useEffect(() => {
        scrollToTop();
    }, [location.pathname, scrollToTop]);

    return (
        <button
            className={style.topButton}
            onClick={scrollToTop}
            style={{ display: isVisible ? 'block' : 'none' }}
            aria-label="Scroll to top"
        >
            <img src={ArrowTop} alt="Scroll to top icon" />
        </button>
    );
};